import { reject } from 'ramda';
import { apolloClient } from '@/apollo';
import {
  CreateMemoDocument,
  DeleteMemoDocument,
  RestoreMemoDocument,
  UpdateMemoDocument,
  AddMemoToGroupDocument,
  RemoveMemoFromGroupDocument,
  DuplicateMemoDocument,
  MemoDefaultFragmentDoc,
} from '@/graphql/operations.gen';

import updateProjectCache from '@utils/updateProjectCache';
import { getTracker } from '@/tracking/trackingPlugin';
import { MemoEvent } from '@/tracking/trackingTypes';
import { getCachedGroup } from '@utils/getCachedGroup';
import { updateMemoInCache, updateGroupContentsInCache } from '@utils/updateEntitiesInCache';

// initial state
const initialState = {};

// getters
const getters = {};

// actions
const actions = {
  /**
   * Create memo.
   */
  create(_context, { projectId, content, comment, name }) {
    return apolloClient
      .mutate({
        mutation: CreateMemoDocument,
        variables: {
          projectId,
          content,
          name,
          comment,
        },
        update(store, response) {
          updateProjectCache(store, projectId, (project) => ({
            memos: [...project.memos, response.data.createMemo],
          }));

          return response;
        },
      })
      .then((data) => {
        getTracker().track(MemoEvent.MemoCreated);

        return data.data.createMemo;
      });
  },

  /**
   * Delete memo.
   */
  delete({}, { id, projectId }) {
    return apolloClient
      .mutate({
        mutation: DeleteMemoDocument,
        variables: {
          id,
        },
        update(store, response) {
          updateProjectCache(store, projectId, (project) => ({
            memos: reject((memo) => memo.id === id, project.memos),
          }));

          return response;
        },
      })
      .then((data) => data.data.deleteMemo);
  },

  /**
   * Restore memo.
   */
  restore({}, { id, projectId }) {
    return apolloClient.mutate({
      mutation: RestoreMemoDocument,
      variables: {
        id,
      },
      update(store, response) {
        updateProjectCache(store, projectId, (project) => ({
          memos: [...project.memos, response.data.restoreMemo],
        }));

        return response;
      },
    });
  },

  /**
   * Update memo.
   */
  update({}, data) {
    const cacheData = apolloClient.readFragment({
      fragment: MemoDefaultFragmentDoc,
      fragmentName: 'MemoDefault',
      id: `Memo:${data.id}`,
    });

    return apolloClient.mutate({
      mutation: UpdateMemoDocument,
      variables: {
        ...data,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateMemo: {
          ...cacheData,
          ...data,
          __typename: 'Memo',
        },
      },
    });
  },

  addToGroup(currentState, { id, group }) {
    const cachedGroup = getCachedGroup(apolloClient, group.id);

    return apolloClient.mutate({
      mutation: AddMemoToGroupDocument,
      variables: {
        id,
        groupId: group.id,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        addToGroup: group,
      },
      update(store, response) {
        const responseData = response.data.addToGroup;

        const memo = updateMemoInCache(store, { id }, (cachedMemo) => {
          if (!cachedMemo.groups.find((sGroup) => sGroup.id === group.id)) {
            return {
              groups: [...cachedMemo.groups, { ...cachedGroup, ...responseData }],
            };
          }

          return {
            groups: cachedMemo.groups,
          };
        });

        updateGroupContentsInCache(store, group.id, (data) => ({
          contents: [...data.contents, memo],
        }));

        return responseData;
      },
    });
  },

  removeFromGroup(currentState, { id, group }) {
    return apolloClient.mutate({
      mutation: RemoveMemoFromGroupDocument,
      variables: {
        id,
        groupId: group.id,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        removeFromGroup: group,
      },
      update(cache, response) {
        const responseData = response.data.removeFromGroup;

        updateMemoInCache(cache, { id }, (memo) => ({
          groups: reject((sGroup) => sGroup.id === responseData.id, memo.groups),
        }));

        updateGroupContentsInCache(cache, group.id, (data) => ({
          contents: data.contents.filter((codeInGroup) => codeInGroup.id !== id),
        }));

        return responseData;
      },
    });
  },

  /**
   * Duplicate memo.
   */
  duplicate({}, { id, projectId }) {
    return apolloClient
      .mutate({
        mutation: DuplicateMemoDocument,
        variables: {
          id,
        },
        update(store, response) {
          updateProjectCache(store, projectId, (project) => ({
            memos: [...project.memos, response.data.duplicateMemo],
          }));

          return response;
        },
      })
      .then((data) => data.data.duplicateMemo);
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
};
